import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import clsx from 'clsx'

import { Card } from '@wartek-id/card'
import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'

import { useApiGetPenilaianProgress } from 'api/kinerja/kepala-sekolah/penilaian'
import StateLoading from 'app/KinerjaKSPage/components/StateLoading'
import { PENGELOLAAN_KINERJA_KS_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import useToggleSdkContext from 'utils/useToogleSdk'
import DialogKonfirmasiUlangDinas from 'app/KinerjaKSPage/components/DialogKonfirmasiUlangDinas'
import { DialogConfirmation } from 'app/KinerjaKSPage/components/CardDataConfirmation'
import { useData } from 'app/KinerjaKSPage/components/CardDataConfirmation/utils/hooks'
import CardPeriodType from 'app/KinerjaShared/components/CardPeriodType'
import CardPeriod from 'app/KinerjaShared/components/CardPeriod'
import { useIsPrior2025 } from 'app/KinerjaShared/hooks/useIsPrior2025'

import type { TStatePengiriman } from 'types/kinerja-ks-pelaksanaan'

const CardProgressPenilaian = ({
  npsn,
  progressPercentage,
}: {
  npsn: string
  progressPercentage: number
}) => {
  const toggleValue = useToggleSdkContext()
  const isPriorTo2025 = useIsPrior2025()
  const isEvaluationPreviewEnabled =
    toggleValue?.['kinerja-ks-dokumen-evaluasi-kinerja-enabled'] || false

  const {
    isLoading: isLoadingAtasan,
    isError: isErrorAtasan,
    atasan,
    block,
  } = useData({
    npsn,
  })

  const {
    data: dataPenilaianProgress,
    isLoading: isLoadingPenilaianProgress,
    isError: isErrorPenilaianProgress,
  } = useApiGetPenilaianProgress({ npsn }, { enabled: !!npsn })

  const progressPenilaian: Record<
    TStatePengiriman,
    { image: string; title: string | React.ReactNode; description?: string }
  > = {
    BELUM_DIKIRIM: {
      image: '/illustrations/img-term-condition-TnC-list.png',
      title: 'Pengelolaan Kinerja Anda sebagai Pegawai selesai!',
      description:
        'Seluruh tahapan sudah berhasil dilakukan. Mohon ditunggu Dokumen Evaluasi Kinerja Anda. Cek lagi halaman ini nanti, ya.',
    },
    PS_TO_DINAS: {
      image: '/illustrations/img-term-condition-TnC-list.png',
      title: 'Pengelolaan Kinerja Anda sebagai Pegawai selesai!',
      description:
        'Seluruh tahapan sudah berhasil dilakukan. Mohon ditunggu Dokumen Evaluasi Kinerja Anda. Cek lagi halaman ini nanti, ya.',
    },
    DINAS_TO_KS: {
      image: '/illustrations/img-document-stamp.png',
      title: (
        <>
          Predikat Kinerja{' '}
          <CardPeriod
            enableMultiplePeriodSupport
            withYear={false}
            separator="-"
            year={dataPenilaianProgress?.meta?.skpYear}
            period={dataPenilaianProgress?.meta?.skpPeriod}
          />
        </>
      ),
    },
  }

  const isLoading = isLoadingAtasan || isLoadingPenilaianProgress

  const isError = isErrorAtasan || isErrorPenilaianProgress

  const statePengiriman = dataPenilaianProgress?.data?.statePengiriman ?? ''

  const showCard =
    progressPercentage === 100 &&
    ['BELUM_DIKIRIM', 'PS_TO_DINAS', 'DINAS_TO_KS'].includes(statePengiriman)

  const showButton =
    isEvaluationPreviewEnabled && ['DINAS_TO_KS'].includes(statePengiriman)

  const shouldShowWarning = !!atasan?.dinas
    ? ['UNCONFIRMED', 'REJECTED'].includes(atasan?.dinas?.status)
    : true

  const [openedDialog, setOpenedDialog] = useState<
    'CONFIRMATION_PROMPT' | 'CONFIRMATION_DIALOG' | null
  >(null)

  if (isLoading) return <StateLoading />

  if (isError || !isPriorTo2025) return null

  if (showCard) {
    return (
      <Card>
        <section
          className={clsx('flex flex-row gap-6 p-6 flex-1', {
            'items-center': statePengiriman === 'DINAS_TO_KS',
            'items-start': statePengiriman !== 'DINAS_TO_KS',
          })}
        >
          <div className="flex-none">
            <Image
              alt=""
              src={progressPenilaian[statePengiriman]?.image}
              width={84}
              height={84}
              className="select-none pointer-events-none"
            />
          </div>

          <div className="flex-1">
            <div className="flex flex-col gap-1 justify-center">
              <Text
                variant="subheading"
                color={
                  statePengiriman === 'DINAS_TO_KS'
                    ? 'subdued'
                    : 'informational'
                }
                className={clsx('!uppercase tracking-[0.5px]', {
                  '!text-blue-60': statePengiriman !== 'DINAS_TO_KS',
                })}
              >
                {statePengiriman === 'DINAS_TO_KS' ? (
                  <>Hasil Penilaian Kinerja Kepala Sekolah</>
                ) : (
                  <CardPeriodType withDate type="pelaksanaan" />
                )}
              </Text>

              <div className="flex items-center gap-2">
                <Text variant="heading-lg">
                  {progressPenilaian[statePengiriman]?.title}
                </Text>
              </div>

              {progressPenilaian[statePengiriman]?.description && (
                <Text color="subdued">
                  {progressPenilaian[statePengiriman]?.description}
                </Text>
              )}
            </div>
          </div>

          <div className="flex-none">
            {/* Should be displayed only if the KS haven't confirm / reject their Dinas */}
            {showButton && shouldShowWarning && (
              <>
                <Button
                  color="blue"
                  size="lg"
                  className="w-60"
                  onClick={() => {
                    setOpenedDialog('CONFIRMATION_PROMPT')
                  }}
                >
                  Cek
                </Button>
              </>
            )}

            {/* Should be displayed only if the KS already confirm their Dinas */}
            {showButton && !shouldShowWarning && (
              <Link
                href={`${PENGELOLAAN_KINERJA_KS_PEGAWAI_PATH}/hasil-evaluasi?id=${npsn}&tab=${dataPenilaianProgress?.meta?.skpPeriod}`}
                passHref
              >
                <Button as="a" color="blue" size="lg" className="w-60">
                  Cek
                </Button>
              </Link>
            )}
          </div>
        </section>

        <DialogKonfirmasiUlangDinas
          isOpen={openedDialog === 'CONFIRMATION_PROMPT'}
          onClick={() => setOpenedDialog('CONFIRMATION_DIALOG')}
          onClose={() => setOpenedDialog(null)}
        />

        {openedDialog === 'CONFIRMATION_DIALOG' && (
          <>
            <DialogConfirmation
              data={atasan}
              isOpen={true}
              isReconfirm={block?.type === 'RECONFIRM'}
              onClose={() => setOpenedDialog(null)}
            />
          </>
        )}
      </Card>
    )
  }

  return null
}

export default CardProgressPenilaian
